import type { KernelTelemetryEnvelope, KernelTelemetrySummary } from '@erp/rpc';

export const KERNEL_POINTER_PATH_CDP_METHODS = [
  'Input.dispatchMouseEvent',
  'Page.captureScreenshot',
] as const;

export function notableKernelTelemetryCount(
  summary: KernelTelemetrySummary | null | undefined,
): number {
  if (!summary) return 0;
  const { notable } = summary;
  return (
    notable.crashes +
    notable.disconnects +
    notable.captchaFailures +
    notable.oomKills
  );
}

export function hasKernelTelemetry(
  input:
    | {
        kernelTelemetryDocumentId?: string | null;
        kernelTelemetrySummary?: KernelTelemetrySummary | null;
      }
    | null
    | undefined,
): boolean {
  return Boolean(
    input?.kernelTelemetryDocumentId || input?.kernelTelemetrySummary,
  );
}

export function isPointerPathTelemetryEvent(
  envelope: KernelTelemetryEnvelope,
): boolean {
  if (envelope.event.type !== 'cdp_command') return false;
  const method = stringField(envelope.event, 'method');
  return (
    method === 'Input.dispatchMouseEvent' || method === 'Page.captureScreenshot'
  );
}

export function filterKernelTelemetryDisplay(
  events: readonly KernelTelemetryEnvelope[],
  options: {
    categories?: readonly string[];
    showPointerPath: boolean;
  },
): KernelTelemetryEnvelope[] {
  const allowed =
    options.categories && options.categories.length > 0
      ? new Set(options.categories)
      : null;
  return events.filter((envelope) => {
    if (allowed && !allowed.has(envelope.event.category)) return false;
    if (!options.showPointerPath && isPointerPathTelemetryEvent(envelope)) {
      return false;
    }
    return true;
  });
}

export function kernelTelemetryMatchesCategory(
  envelope: KernelTelemetryEnvelope,
  category: string | undefined,
): boolean {
  return category === undefined || envelope.event.category === category;
}

export function kernelTelemetryEventTime(
  envelope: KernelTelemetryEnvelope,
): Date | null {
  const record = envelope as Record<string, unknown>;
  const event = envelope.event as Record<string, unknown>;
  for (const value of [
    record.ts,
    record.timestamp,
    event.ts,
    event.timestamp,
  ]) {
    const parsed = parseTime(value);
    if (parsed) return parsed;
  }
  return null;
}

export function kernelTelemetryEventDetail(
  envelope: KernelTelemetryEnvelope,
): string | null {
  const event = envelope.event as Record<string, unknown>;
  const parts: string[] = [];
  for (const key of ['method', 'status', 'code', 'reason', 'kind']) {
    const value = event[key];
    if (typeof value === 'string' && value) parts.push(value);
    else if (typeof value === 'number') parts.push(String(value));
  }
  return parts.length > 0 ? parts.join('  ') : null;
}

export function mergeKernelTelemetryEvents(
  existing: readonly KernelTelemetryEnvelope[],
  incoming: readonly KernelTelemetryEnvelope[],
): KernelTelemetryEnvelope[] {
  const bySeq = new Map<number, KernelTelemetryEnvelope>();
  for (const envelope of existing) bySeq.set(envelope.seq, envelope);
  for (const envelope of incoming) bySeq.set(envelope.seq, envelope);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export type KernelTelemetryTranslate = (
  message: string,
  options?: Record<string, unknown>,
) => string;

export type NotableTelemetryKind = 'captcha' | 'disconnect' | 'crash' | 'oom';

const NOTABLE_KIND_COPY: Record<
  NotableTelemetryKind,
  { one: string; many: string; contextOne: string; contextMany: string }
> = {
  captcha: {
    one: 'captcha failed',
    many: 'captcha failed ×{count}',
    contextOne: 'kernel telemetry notable captcha failure',
    contextMany: 'kernel telemetry notable captcha failure count',
  },
  disconnect: {
    one: 'cdp disconnect',
    many: 'cdp disconnect ×{count}',
    contextOne: 'kernel telemetry notable cdp disconnect',
    contextMany: 'kernel telemetry notable cdp disconnect count',
  },
  crash: {
    one: 'service crashed',
    many: 'service crashed ×{count}',
    contextOne: 'kernel telemetry notable chrome crash',
    contextMany: 'kernel telemetry notable chrome crash count',
  },
  oom: {
    one: 'oom kill',
    many: 'oom kill ×{count}',
    contextOne: 'kernel telemetry notable oom kill',
    contextMany: 'kernel telemetry notable oom kill count',
  },
};

export function formatNotableTelemetryKind(
  gt: KernelTelemetryTranslate,
  item: { kind: NotableTelemetryKind; count: number },
): string {
  const copy = NOTABLE_KIND_COPY[item.kind];
  if (item.count === 1) {
    return gt(copy.one, { context: copy.contextOne });
  }
  return gt(copy.many, {
    context: copy.contextMany,
    count: String(item.count),
  });
}

export function formatKernelTelemetryCollapsedCount(
  gt: KernelTelemetryTranslate,
  summary: KernelTelemetrySummary,
): string {
  const notable = notableKernelTelemetryCount(summary);
  if (notable > 0) {
    return gt('{eventCount} events  ·  {notable} notable', {
      context: 'kernel telemetry collapsed summary counts',
      eventCount: String(summary.eventCount),
      notable: String(notable),
    });
  }
  return gt('{eventCount} events', {
    context: 'kernel telemetry collapsed event count',
    eventCount: String(summary.eventCount),
  });
}

export function formatKernelTelemetryListCount(
  gt: KernelTelemetryTranslate,
  options: {
    summary: KernelTelemetrySummary | null;
    shown: number;
    hiddenPointerCount: number;
    showPointerPath: boolean;
  },
): string {
  if (options.hiddenPointerCount > 0 && !options.showPointerPath) {
    return gt('{total} events  ·  showing {shown} (pointer path hidden)', {
      context: 'kernel telemetry list counts with pointer-path filter',
      total: String(options.summary?.eventCount ?? options.shown),
      shown: String(options.shown),
    });
  }
  return gt('{count} events', {
    context: 'kernel telemetry list count',
    count: String(options.summary?.eventCount ?? options.shown),
  });
}

export function notableKernelTelemetryKinds(
  summary: KernelTelemetrySummary | null | undefined,
): Array<{ kind: NotableTelemetryKind; count: number }> {
  if (!summary) return [];
  const items: Array<{ kind: NotableTelemetryKind; count: number }> = [];
  if (summary.notable.captchaFailures > 0) {
    items.push({ kind: 'captcha', count: summary.notable.captchaFailures });
  }
  if (summary.notable.disconnects > 0) {
    items.push({ kind: 'disconnect', count: summary.notable.disconnects });
  }
  if (summary.notable.crashes > 0) {
    items.push({ kind: 'crash', count: summary.notable.crashes });
  }
  if (summary.notable.oomKills > 0) {
    items.push({ kind: 'oom', count: summary.notable.oomKills });
  }
  return items;
}

function stringField(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

function parseTime(value: unknown): Date | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string' && value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}
